import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

var _templateObject;

import React from 'react';
import classes from '@nerdwallet/base-styles/classes';
import { gql } from '@nerdwallet/apollo';
import { getCreditScore } from '@nerdwallet/js-auth-tools/selectors';
import { getApprovalOdds, getLegacyId, getMaximumFICO, getMinimumFICO } from '@nerdwallet/marketplace/selectors/credit-cards';
import { setTrk, TrkTag, TrkElement, TrkPageType } from '@nerdwallet/nw-client-lib/trk-helper';
import { ElementInteraction } from '@nerdwallet/nw-client-lib/tracking';
import { Button, Link, Title, Tooltip, Type } from '@nerdwallet/currency';
import { calculateBucket, getCreditScoreComparisonContent } from '@nerdwallet/likelihood-of-approval';
import { handleAuthClickDefault } from '../likelihood-of-approval-cta';
import styles from "../marketplace-product-card.module.css";

var UnlockedWithTU = function UnlockedWithTU(_ref) {
  var creditScoreContent = _ref.creditScoreContent,
      url = _ref.url;
  return /*#__PURE__*/React.createElement(Type, {
    className: classes('color-neutral-darkest')
  }, url, /*#__PURE__*/React.createElement(Title, {
    size: "x-small",
    className: classes('margin-bottom-3', 'margin-top-3')
  }, "You're ".concat(creditScoreContent.pointDifference, " ").concat(creditScoreContent.relativeScoreLabel, " suggested credit score of ").concat(creditScoreContent.scoreToCompare, ".")), "But, heads up, being within the recommended range doesn't guarantee approval. Credit score is only one factor lenders consider and they may not use the TransUnion Vantage Score 3.0 like we do.");
};

if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line global-require
  var PropTypes = require('prop-types');

  UnlockedWithTU.propTypes = {
    creditScoreContent: PropTypes.shape({
      pointDifference: PropTypes.string,
      relativeScoreLabel: PropTypes.string,
      scoreToCompare: PropTypes.number
    }),
    url: PropTypes.string
  };
}

var matchScoreTextWithCTA = function matchScoreTextWithCTA(url) {
  return /*#__PURE__*/React.createElement(React.Fragment, null, "To calculate your NerdWallet match score, we analyzed your preferences, credit score, and the cards already in your wallet. ", /*#__PURE__*/React.createElement(Link, {
    href: url
  }, "Edit answers"));
};

var MATCH_SCORE_INFO_TEXT = /*#__PURE__*/React.createElement(Type, null, "What do lenders look for? Credit score among other factors. But heads up, they may not use the TransUnion Vantage Score 3.0 like we do, and these odds don't guarantee approval or denial.");

var unlockedWithTUandApprovalOdds = function unlockedWithTUandApprovalOdds(approvalOdds, url) {
  return /*#__PURE__*/React.createElement(Type, {
    className: classes('color-neutral-darkest')
  }, matchScoreTextWithCTA(url), /*#__PURE__*/React.createElement(Title, {
    size: "x-small",
    className: classes('margin-bottom-3', 'margin-top-3')
  }, "".concat(calculateBucket(approvalOdds).percentile / 10, "\n      in 10 Nerdwallet members (with credit like yours) were approved for this card.")), MATCH_SCORE_INFO_TEXT);
};

var unlockedWithoutTU = function unlockedWithoutTU(handleAuthCTAClick, legacyId, url) {
  return /*#__PURE__*/React.createElement(Type, {
    className: classes('color-neutral-darkest')
  }, matchScoreTextWithCTA(url), /*#__PURE__*/React.createElement(Title, {
    size: "x-small",
    className: classes('margin-top-3', 'margin-bottom-3')
  }, "Your NerdWallet match score could be more accurate with your exact credit score."), MATCH_SCORE_INFO_TEXT, /*#__PURE__*/React.createElement(Button, {
    onClick: function onClick() {
      return handleAuthCTAClick(legacyId);
    },
    className: styles['match-score-get-cs-cta']
  }, "GET MY CREDIT SCORE"));
};

var BaseMatchScoreToolTip = function BaseMatchScoreToolTip(_ref2) {
  var _params;

  var analytics = _ref2.analytics,
      approvalOdds = _ref2.approvalOdds,
      creditScore = _ref2.creditScore,
      creditScoreContent = _ref2.creditScoreContent,
      handleAuthCTAClick = _ref2.handleAuthCTAClick,
      legacyId = _ref2.legacyId,
      taxonomy = _ref2.taxonomy;
  var url = setTrk({
    url: "/recommend?askCreditScore=true&matchScoreCTACardId=".concat(legacyId),
    taxonomy: taxonomy,
    params: (_params = {}, _defineProperty(_params, TrkTag.element, TrkElement.hyperlink), _defineProperty(_params, TrkTag.location, "match-score-tooltip-popover--prodId".concat(legacyId)), _defineProperty(_params, TrkTag.pageType, TrkPageType.marketPlace), _params)
  });
  var popoverContent;

  if (creditScore && approvalOdds) {
    popoverContent = unlockedWithTUandApprovalOdds(approvalOdds, url);
  } else if (creditScore) {
    popoverContent = /*#__PURE__*/React.createElement(UnlockedWithTU, {
      creditScoreContent: creditScoreContent,
      url: matchScoreTextWithCTA(url)
    });
  } else {
    popoverContent = unlockedWithoutTU(handleAuthCTAClick, legacyId, url);
  }

  return /*#__PURE__*/React.createElement(ElementInteraction, {
    id: "match-score-tooltip-prodId".concat(legacyId),
    elementName: "Product Card Match Score Tooltip",
    type: "HOVER"
  }, /*#__PURE__*/React.createElement(Tooltip, {
    analytics: {
      entityName: (analytics === null || analytics === void 0 ? void 0 : analytics.entityName) || "product_card_match_score_tooltip_".concat(legacyId)
    },
    id: "ex1",
    title: "How is my match score calculated?",
    content: /*#__PURE__*/React.createElement("div", {
      className: styles.matchScoreTooltipPopoverContent
    }, popoverContent),
    className: classes('color-neutral-darkest', 'type-2', 'margin-top-0')
  }));
};

if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line global-require
  var _PropTypes = require('prop-types');

  BaseMatchScoreToolTip.propTypes = {
    analytics: _PropTypes.shape({
      entityName: _PropTypes.string
    }),
    approvalOdds: _PropTypes.number,
    creditScore: _PropTypes.number,
    creditScoreContent: _PropTypes.shape({
      pointDifference: _PropTypes.string,
      relativeScoreLabel: _PropTypes.string,
      scoreToCompare: _PropTypes.number
    }),
    legacyId: _PropTypes.number,
    handleAuthCTAClick: _PropTypes.func,
    taxonomy: _PropTypes.object.isRequired // eslint-disable-line react/forbid-prop-types

  };
}

export var GraphqlMatchScoreToolTip = function GraphqlMatchScoreToolTip(_ref3) {
  var analytics = _ref3.analytics,
      approvalLikelihood = _ref3.approvalLikelihood,
      handleAuthCTAClick = _ref3.handleAuthCTAClick,
      identity = _ref3.identity,
      marketplaceEntity = _ref3.marketplaceEntity,
      taxonomy = _ref3.taxonomy;
  var creditScoreComparisonContent = marketplaceEntity.creditScoreComparisonContent,
      legacyId = marketplaceEntity.legacyId;
  return /*#__PURE__*/React.createElement(BaseMatchScoreToolTip, {
    analytics: analytics,
    approvalOdds: approvalLikelihood,
    creditScore: getCreditScore(identity),
    creditScoreContent: creditScoreComparisonContent,
    handleAuthCTAClick: handleAuthCTAClick,
    legacyId: legacyId,
    taxonomy: taxonomy
  });
};
GraphqlMatchScoreToolTip.fragment = gql(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  fragment GraphqlMatchScoreToolTip on MarketplaceEntityInterface {\n    ... on CreditCardsOfferV2 {\n      creditScoreComparisonContent {\n        pointDifference\n        relativeScoreLabel\n        scoreToCompare\n      }\n      legacyId\n    }\n  }\n"])));

if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line global-require
  var _PropTypes2 = require('prop-types');

  GraphqlMatchScoreToolTip.propTypes = {
    analytics: _PropTypes2.shape({
      entityName: _PropTypes2.string
    }),
    approvalLikelihood: _PropTypes2.number,
    handleAuthCTAClick: _PropTypes2.func,
    identity: _PropTypes2.oneOfType([_PropTypes2.shape({
      vantageScore3: _PropTypes2.number
    }), _PropTypes2.shape({
      profiles: _PropTypes2.shape({
        tu_credit_report_profile: _PropTypes2.shape({
          vantage_score_3: _PropTypes2.number
        })
      })
    })]),
    marketplaceEntity: _PropTypes2.shape({
      creditScore: _PropTypes2.number,
      creditScoreComparisonContent: _PropTypes2.shape({
        pointDifference: _PropTypes2.string,
        relativeScoreLabel: _PropTypes2.string,
        scoreToCompare: _PropTypes2.number
      }),
      legacyId: _PropTypes2.number,
      maxFico: _PropTypes2.number,
      minFico: _PropTypes2.number
    }),
    taxonomy: _PropTypes2.object.isRequired // eslint-disable-line react/forbid-prop-types

  };
}

var MatchScoreToolTip = function MatchScoreToolTip(_ref4) {
  var analytics = _ref4.analytics,
      identity = _ref4.identity,
      marketplaceEntity = _ref4.marketplaceEntity,
      handleAuthCTAClick = _ref4.handleAuthCTAClick,
      taxonomy = _ref4.taxonomy;
  var creditScore = getCreditScore(identity);
  return /*#__PURE__*/React.createElement(BaseMatchScoreToolTip, {
    analytics: analytics,
    approvalOdds: getApprovalOdds(marketplaceEntity),
    creditScore: creditScore,
    creditScoreContent: getCreditScoreComparisonContent(creditScore, getMaximumFICO(marketplaceEntity), getMinimumFICO(marketplaceEntity)),
    handleAuthCTAClick: handleAuthCTAClick,
    legacyId: getLegacyId(marketplaceEntity),
    taxonomy: taxonomy
  });
};

if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line global-require
  var _PropTypes3 = require('prop-types');

  MatchScoreToolTip.propTypes = {
    analytics: _PropTypes3.shape({
      entityName: _PropTypes3.string
    }),
    identity: _PropTypes3.object.isRequired,
    // eslint-disable-line react/forbid-prop-types
    marketplaceEntity: _PropTypes3.object.isRequired,
    // eslint-disable-line react/forbid-prop-types
    handleAuthCTAClick: _PropTypes3.func,
    taxonomy: _PropTypes3.object.isRequired // eslint-disable-line react/forbid-prop-types

  };
}

MatchScoreToolTip.defaultProps = {
  analytics: {},
  handleAuthCTAClick: function handleAuthCTAClick(legacyId) {
    return handleAuthClickDefault({
      trk_element: 'CC Match Score Tooltip',
      trk_location: "match-score-tooltip--prodId".concat(legacyId)
    });
  }
};
export default MatchScoreToolTip;